'use client';

import { FAQItem } from '@c/FAQSection';
import ItemsToSellCard, { ItemsToSellType } from '@c/ItemsToSellCard';
import WhySellOnMxLocker, { SellingReasonType } from '@c/WhySellOnMxLocker';
import SellerReviews from '@c/becomeASeller/SellerReviews';
import SellYourGearHeader from '@c/headers/SellYourGearHeader';
import { MxGetPaid, MxListing, MxShipping } from '@c/icons';
import { MxRacer } from '@c/icons/mxRacer';
import { MxSellerProtection } from '@c/icons/mxSellerProtection';
import StarBuyers from '@i/sell-your-gear/5starBuyers.png';
import Community from '@i/sell-your-gear/community.png';
import GearsImage from '@i/sell-your-gear/gears.png';
import ReadytoSellMobile from '@i/sell-your-gear/readyToSellMobile.png';
import ReadytoSellWeb from '@i/sell-your-gear/readytoSellWeb.png';
import Secure from '@i/sell-your-gear/secure.png';
import Support from '@i/sell-your-gear/support.png';
import { ProductDocument } from '@models/product';
import Accordion from '@ui/Accordion';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import Image from 'next/image';
import Link from 'next/link';
import RecentlySoldSection from './RecentlySoldSection';

type HowItWorksProps = {
  recentlySold: ProductDocument[];
}; // App router todo: recently sold should be server side rendered (visible in page source)

const HowItWorks = ({ recentlySold }: HowItWorksProps) => (
  <>
    {/* header */}

    <SellYourGearHeader />
    {/* main body */}
    <div className="w-full">
      {/* youtube embed + paragraph */}

      {/* how it works section */}
      <div
        id="howitworks"
        className="mx-auto mt-[6rem] max-w-default px-[1rem] text-center sm:px-0"
      >
        <div className="flex flex-col">
          <h1 className="text-[3.2rem] font-semibold">Selling, made easy.</h1>
          <h2 className="mt-[1.6rem] text-[1.6rem] font-normal">
            Sell (almost) anything. From riding gear, bike parts, to dirt bikes.
            New or used.
          </h2>
        </div>

        <div className="mx-auto mt-[6rem] flex max-w-[120rem] flex-wrap justify-center gap-[2.4rem] md:mt-[10rem]">
          <div className="flex max-w-[38.4rem] flex-col items-start p-[1.6rem]">
            <MxListing />
            <div className="mt-[3rem] flex items-center gap-[1.2rem] md:mt-[4rem]">
              <h5 className="text-[2.4rem] font-semibold">Create a listing</h5>
            </div>
            <div className="mb-[4rem] mt-[2.4rem] text-left text-[1.6rem]">
              Sign up, list your items for free, and reach riders from around
              the world.
            </div>
          </div>
          <div className="flex max-w-[38.4rem] flex-col items-start p-[1.6rem]">
            <MxShipping />
            <div className="mt-[3rem] flex items-center gap-[1.2rem] md:mt-[4rem]">
              <h5 className="text-[2.4rem] font-semibold">Ship your order</h5>
            </div>
            <div className="mb-[4rem] mt-[2.4rem] text-left text-[1.6rem]">
              Once a customer has purchased your item, print a label with us or
              provide your own tracking.
            </div>
          </div>
          <div className="flex max-w-[38.4rem] flex-col items-start p-[1.6rem]">
            <MxGetPaid />
            <div className="mt-[3rem] flex items-center gap-[1.2rem] md:mt-[4rem]">
              <h5 className="text-[2.4rem] font-semibold">Get paid</h5>
            </div>
            <div className="mb-[4rem] mt-[2.4rem] text-left text-[1.6rem]">
              Listing is free. We only charge a fee when your sale is complete.
            </div>
          </div>
        </div>
      </div>

      {/* banner */}
      <div className="relative mt-[5.5rem] w-full bg-brand-light-dark-gray  px-[1.6rem]  text-brand-black sm:h-[27.2rem] sm:px-0 md:mt-[10rem]">
        <div className="mx-auto flex h-full max-w-[120rem] flex-col items-center justify-between gap-[2.4rem] sm:flex-row sm:gap-[12.6rem]">
          {/* left */}
          <div className="relative h-full md:bottom-[3rem] md:h-[150%]">
            <Gears />
          </div>
          {/* right */}
          <div className="flex-1">
            <div className="flex flex-col items-center p-[2.4rem] text-[3.2rem] font-semibold md:items-start">
              <div className="w-full text-center md:text-left">
                Not using it?
              </div>
              <div className="w-full text-center md:text-left">
                Let&apos;s sell it.
              </div>
              <div className="mt-[2.2rem] inline  content-center ">
                <Button
                  type="secondary"
                  text="Start selling"
                  width="fixed"
                  href="/become-a-seller"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* what we have */}
      <div className="mx-auto flex max-w-[120rem] flex-col items-center gap-[6rem] px-[1rem] pt-[10rem] sm:px-0">
        <div className="flex max-w-[80rem] flex-col">
          <div className="text-center text-[3.2rem] font-semibold">
            MX Locker has the lowest fees in the market.
          </div>
          <div className="mt-[1.6rem] text-center text-[1.6rem] font-normal">
            Listing an item is always free on MX Locker. Fees are charged when
            an item sells or when a service is used. The following chart
            explains fees on MX Locker.
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="mt-[5rem] flex w-full flex-col justify-stretch gap-0 md:flex-row">
            {itemsToSell.map(({ Icon, name, percent }) => (
              <ItemsToSellCard
                Icon={Icon}
                key={name}
                name={name}
                percent={percent}
              />
            ))}
          </div>
          <span>
            *An additional 3% processing fee is charged by our payment provider.
          </span>
        </div>

        <div className="text-[2.2rem]">
          Built for <span className="text-brand-secondary">Riders</span>, by{' '}
          <span className="text-brand-secondary">Riders</span>.
        </div>
      </div>

      {/* Why sell on MxLocker */}
      <div className="mx-auto flex max-w-[120rem] flex-col items-center gap-[6rem] px-[1rem] py-[6rem] sm:px-0 md:py-[10rem]">
        <div className="flex max-w-[80rem] flex-col">
          <div className="text-center text-[3.2rem] font-semibold">
            Why sell on MX Locker?
          </div>
        </div>
        <div className="mt-[5rem] flex w-full flex-col flex-wrap justify-stretch gap-[2.4rem] md:flex-row">
          {sellingReasons.map(({ image, name, description }) => (
            <WhySellOnMxLocker
              image={image}
              key={name}
              name={name}
              description={description}
            />
          ))}
        </div>
      </div>

      <SellerReviews />

      <div className="py-[6rem] md:py-[10rem]">
        <div className="mx-auto flex max-w-[120rem] flex-col items-center gap-[3rem] px-[1rem] sm:px-0">
          <div className="flex max-w-[80rem] flex-col">
            <div className="text-center text-[3.2rem] font-semibold">
              How does MX Locker help sellers?
            </div>
            <div className="mt-[1.6rem] text-center text-[1.6rem] font-normal">
              MX Locker was built for riders, by riders. Reach a targeted
              audience, and discover tools and resources to help drive online
              sales from around the world.
            </div>
          </div>

          <div className="mx-auto mt-[6rem] flex max-w-[120rem] flex-wrap justify-center gap-[2.4rem] md:mt-[10rem]">
            <div className="flex max-w-[38.4rem] flex-col items-start p-[1.6rem]">
              <MxSellerProtection />
              <div className="mt-[3rem] flex items-center gap-[1.2rem] md:mt-[4rem]">
                <h5 className="text-[2.4rem] font-semibold">
                  MX Locker Seller Protection
                </h5>
              </div>
              <div className="mb-[4rem] mt-[2.4rem] text-left text-[1.6rem]">
                As a seller on MX Locker, you are fully protected against fraud
                and chargebacks.
              </div>
            </div>
            <div className="flex max-w-[38.4rem] flex-col items-start p-[1.6rem]">
              <MxRacer />
              <div className="mt-[3rem] flex items-center gap-[1.2rem] md:mt-[4rem]">
                <h5 className="text-[2.4rem] font-semibold">
                  For Riders, by Riders.{' '}
                </h5>
              </div>
              <div className="mb-[4rem] mt-[2.4rem] text-left text-[1.6rem]">
                Reach a broad, targeted audience and increase your sales.
              </div>
            </div>
            <div className="flex max-w-[38.4rem] flex-col items-start p-[1.6rem]">
              <MxShipping />
              <div className="mt-[3rem] flex items-center gap-[1.2rem] md:mt-[4rem]">
                <h5 className="text-[2.4rem] font-semibold">
                  Shipping with MX Locker
                </h5>
              </div>
              <div className="mb-[4rem] mt-[2.4rem] text-left text-[1.6rem]">
                Live tracking for your customers, discounted shipping labels,
                and a seamless shipping experience.
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <Button
              type="secondary"
              text="Become a Seller"
              href="/become-a-seller"
              width="fixed"
            />
          </div>
        </div>
      </div>

      <div className="w-full bg-brand-secondary p-[10rem]">
        <div className="mx-auto flex max-w-[120rem] flex-col items-center justify-between gap-[3rem] px-[1rem] text-white sm:px-0 md:flex-row">
          <div className="flex flex-col">
            <div className="flex-1 text-center text-[4rem]">150k+</div>
            <div className="flex-1 text-center text-[1.6rem]">Members</div>
          </div>
          <div className="flex flex-col">
            <div className="flex-1 text-center text-[4rem]">100+</div>
            <div className="flex-1 text-center text-[1.6rem]">
              New items listed everyday
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex-1 text-center text-[4rem]">4.9</div>
            <div className="flex-1 text-center text-[1.6rem]">
              Average app rating
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex-1 text-center text-[4rem]">7M+</div>
            <div className="flex-1 text-center text-[1.6rem]">
              Earned by sellers
            </div>
          </div>
        </div>
      </div>

      <RecentlySoldSection recentlySold={recentlySold} />
      {/* Why sell on MxLocker */}
      <div className="mx-auto flex max-w-[120rem] flex-col items-center justify-between gap-[2.4rem] px-[1.6rem] py-[6rem] sm:px-0 md:flex-row md:py-[10rem]">
        <div className="flex-1 text-left text-[3.2rem] font-semibold">
          Frequently <br className="hidden md:block" /> Asked Questions
        </div>
        <div className="flex-1">
          <div className="flex  w-full flex-col gap-[4rem]">
            <FAQSection />
            <div className="text-[1.6rem] font-semibold">
              Still have questions? feel free to{' '}
              <Link href="/contact" className="text-brand-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="hidden md:block">
          <SafeImage
            src={ReadytoSellWeb}
            alt="header image"
            className="w-full"
          />
        </div>
        <div className="md:hidden">
          <SafeImage
            src={ReadytoSellMobile}
            alt="header image"
            className="w-full"
          />
        </div>
        <div className="absolute inset-0 top-[30%]">
          <div className="flex flex-col items-center font-semibold text-brand-white">
            <div className="mb-[4rem] w-full text-center text-[3.2rem] md:text-[4rem]">
              Ready to start selling? Let’s get started!
            </div>
            <Button
              type="secondary"
              text="Become a Seller"
              width="fixed"
              href="/become-a-seller"
            />
          </div>
        </div>
      </div>
    </div>
  </>
);

const Gears = () => {
  return <SafeImage className="w-full" alt={'GearsImage'} src={GearsImage} />;
};

const itemsToSell: ItemsToSellType[] = [
  {
    Icon: (
      <Image
        src={'https://cdn.mxlocker.com/categories%2Fthumb%40500_gear_icon.png'}
        alt="riding gear"
        width={180}
        height={180}
      />
    ),
    name: 'Riding Gear*',
    percent: 90,
  },
  {
    Icon: (
      <Image
        src={'https://cdn.mxlocker.com/categories%2Fthumb%40500_parts_icon.png'}
        alt="bike parts"
        width={180}
        height={180}
      />
    ),
    name: 'Bike Parts*',
    percent: 90,
  },
  {
    Icon: (
      <Image
        src={'https://cdn.mxlocker.com/categories/mx_dirtbike_icon.svg'}
        alt="MX Dirt Bikes"
        width={180}
        height={180}
      />
    ),
    name: 'Dirt Bikes',
    percent: 100,
  },
];

const sellingReasons: SellingReasonType[] = [
  {
    image: Community,
    name: 'Built for the dirt bike community',
    description:
      'Created by riders, MX Locker was  was designed specifically for the dirt bike industry.',
  },
  {
    image: StarBuyers,
    name: '5-star buyer community',
    description:
      'Millions of high-quality prospective buyers from all over the world search for gear on MX Locker.',
  },
  {
    image: Secure,
    name: 'Secure transactions & shipping',
    description:
      'You can feel confident selling on a trusted platform with hassle-free shipping options.',
  },
  {
    image: Support,
    name: '24/7 MX support',
    description:
      'Our team of fellow riders is ready and available to help you around the clock.',
  },
];

const FAQs: FAQItem[] = [
  {
    q: 'What types of products can I sell on MX Locker?',
    a: 'MX Locker is a specialized marketplace for motocross and dirt bike enthusiasts, so we only allow products related to these activities. This includes things like protective gear, apparel, accessories, and parts.',
  },
  {
    q: 'How do I create a seller account on MX Locker?',
    a: 'To become a seller on MX Locker, simply visit our website and click on the “Sell on MX Locker” button. Follow the prompts to create an account, and then you’ll be able to start listing your products for sale.',
  },
  {
    q: 'Is it free to sell on MX Locker?',
    a: 'Yes, creating a seller account on MX Locker is free. However, there are fees associated with selling products on the platform. These include a transaction fee and a commission fee, which vary depending on the product category.',
  },
  {
    q: 'How do I list my products for sale on MX Locker?',
    a: 'Once you’ve created a seller account, you can start listing your products by logging into your account and selecting the “Create a Listing” option. You’ll need to provide information about the product, including a description, photos, and pricing.',
  },
  {
    q: 'How do I manage my sales on MX Locker?',
    a: 'MX Locker provides a seller dashboard that allows you to manage your sales, track your earnings, and communicate with buyers. You’ll receive notifications when you make a sale, and you can use the dashboard to update your product listings, process orders, and more.',
  },
  {
    q: 'What happens if there’s an issue with a sale or a buyer?',
    a: 'MX Locker has a dispute resolution process in place to help sellers and buyers resolve any issues that may arise during a transaction. Our team is available to assist with disputes and can provide guidance on how to resolve the issue fairly.',
  },
];

const FAQSection = () => {
  return (
    <div className="flex w-full flex-col gap-[1.6rem]">
      {FAQs.map((faq) => (
        <Accordion
          divider={true}
          highlightHeaderOnOpen={true}
          hover="text"
          intent="faq"
          key={faq.q}
          title={faq.q}
        >
          <p className="pl-[1.6rem]">{faq.a}</p>
        </Accordion>
      ))}
    </div>
  );
};

export default HowItWorks;
